import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { useNetwork } from '../../contexts/NetworkContext'
import { IdCard } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { AdvertisersApi, Advertiser } from '@/services/advertisers/advertisersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { AdvertiserTable } from '../../components/AdvertiserTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { AdvertiserFilters, DEFAULT_ADVERTISER_FILTERS, type AdvertiserFilterState } from '../../components/AdvertiserFilters'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardIcon } from '@/components/ui/card'
import { ListPagination } from '../../components/ListPagination'
import { getContext } from '@/services/networks/networksApi'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function AdvertisersPage() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady } = useNetwork()
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()

  const [stored, setFilters] = usePersistedState<AdvertiserFilterState>('filters.advertisers', DEFAULT_ADVERTISER_FILTERS)
  // A remembered value predates any filter added since it was stored, so the
  // defaults fill in what it does not carry.
  const filters: AdvertiserFilterState = { ...DEFAULT_ADVERTISER_FILTERS, ...stored }
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<Advertiser[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const includeInactive = filters.objectState !== 'ACTIVE'
  const context = getContext()

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    AdvertisersApi.getPage(page, pageSize, includeInactive, search, filters.bidding, sort)

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['advertisers', context, scopeKey, filters, search, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken && scopeReady,
  })

  const advertisers = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  // Reset to page 1 when filter changes
  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const handleFilters = (v: AdvertiserFilterState) => {
    setFilters(v)
    setPage(1)
    setSelection([])
  }

  const handleSearch = (v: string) => {
    setSearch(v)
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((a) => AdvertisersApi.update(a.id, { name: a.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['advertisers', context] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'advertisers.doneDetail_activate' : 'advertisers.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('advertisers.title') }]}>

      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('advertisers.title')}</h1>
            <p>{t('advertisers.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="advertiser" size="default" />
          </div>
        </div>

        {error && (
          <div className="auth-form__error" style={{ marginBottom: 16 }}>
            <span className="pi pi-exclamation-circle" />
            {(error as Error).message}
          </div>
        )}

        <ListCard fetchAll={() => walkAllPages(fetchPage)}>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={IdCard} />
                {t('advertisers.title')}
              </>
            }
            count={totalCount}
            controls={
              <>
                <SearchInput value={search} onChange={handleSearch} />
                <AdvertiserFilters filters={filters} onChange={handleFilters} />
              </>
            }
            actions={<PageSizeSelect value={pageSize} onChange={(s) => { setPageSize(s); setPage(1) }} />}
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <AdvertiserTable
              advertisers={advertisers}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={
                !hasToken
                  ? t('common.signInToLoad')
                  : search
                    ? t('common.noSearchResults', { search })
                    : t('advertisers.noAdvertisersFound')
              }
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('advertisers.advertiserCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
